import React, { FC, useEffect, useState } from "react";
import { Box, Skeleton, Accordion } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { PropertiesWithConfig, SalesItemTranslated } from "@/types";
import styles from "../styles.module.css";
import { useTranslation } from "react-i18next";
import NumberInput from "@/components/atoms/NumberInput";
import CTA from "@/components/atoms/CTA";
import Checkbox from "@/components/atoms/Checkbox";

interface FilterPanelProps {
  salesItems: SalesItemTranslated[] | undefined;
  isLoadingSalesItems: boolean;
  selectedSalesItemId: number | null;
  onSalesItemChange: (salesItemId: number) => void;

  dynamicProperties: PropertiesWithConfig[] | undefined;
  isLoadingProperties: boolean;
  selectedPropertyIds: number[];
  onPropertyChange: (propertyId: number) => void;

  selectedOrderTypes: number[];
  onOrderTypeChange: (orderTypeId: number) => void;

  minAmount: number | undefined;
  setMinAmount: (value: number | undefined) => void;
  maxAmount: number | undefined;
  setMaxAmount: (value: number | undefined) => void;
  minPrice: number | undefined;
  setMinPrice: (value: number | undefined) => void;
  maxPrice: number | undefined;
  setMaxPrice: (value: number | undefined) => void;

  onClearAll: () => void;
}

const FilterPanel: FC<FilterPanelProps> = ({
  salesItems,
  isLoadingSalesItems,
  selectedSalesItemId,
  onSalesItemChange,
  dynamicProperties,
  isLoadingProperties,
  selectedPropertyIds,
  onPropertyChange,
  selectedOrderTypes,
  onOrderTypeChange,
  minAmount,
  setMinAmount,
  maxAmount,
  setMaxAmount,
  minPrice,
  setMinPrice: _setMinPrice,
  maxPrice,
  setMaxPrice: _setMaxPrice,
  onClearAll,
}) => {
  const { t } = useTranslation();

  // Local state for number inputs - debounced before propagating to parent
  const [localMinAmount, setLocalMinAmount] = useState<number | string | undefined>(minAmount);
  const [localMaxAmount, setLocalMaxAmount] = useState<number | string | undefined>(maxAmount);
  // const [localMinPrice, setLocalMinPrice] = useState<number | string | undefined>(minPrice);
  // const [localMaxPrice, setLocalMaxPrice] = useState<number | string | undefined>(maxPrice);

  const [debouncedMinAmount] = useDebouncedValue(localMinAmount, 500);
  const [debouncedMaxAmount] = useDebouncedValue(localMaxAmount, 500);
  // const [debouncedMinPrice] = useDebouncedValue(localMinPrice, 500);
  // const [debouncedMaxPrice] = useDebouncedValue(localMaxPrice, 500);

  // Propagate debounced values to parent
  useEffect(() => {
    setMinAmount(debouncedMinAmount === "" ? undefined : (debouncedMinAmount as number | undefined));
  }, [debouncedMinAmount]);
  useEffect(() => {
    setMaxAmount(debouncedMaxAmount === "" ? undefined : (debouncedMaxAmount as number | undefined));
  }, [debouncedMaxAmount]);
  // useEffect(() => { setMinPrice(debouncedMinPrice === "" ? undefined : debouncedMinPrice as number | undefined); }, [debouncedMinPrice]);
  // useEffect(() => { setMaxPrice(debouncedMaxPrice === "" ? undefined : debouncedMaxPrice as number | undefined); }, [debouncedMaxPrice]);

  // Sync local state when parent clears (e.g. clear all button)
  useEffect(() => {
    if (minAmount === undefined) setLocalMinAmount(undefined);
  }, [minAmount]);
  useEffect(() => {
    if (maxAmount === undefined) setLocalMaxAmount(undefined);
  }, [maxAmount]);
  // useEffect(() => { if (minPrice === undefined) setLocalMinPrice(undefined); }, [minPrice]);
  // useEffect(() => { if (maxPrice === undefined) setLocalMaxPrice(undefined); }, [maxPrice]);

  const orderTypeOptions = [
    { id: 1, label: t("actions:buy") },
    { id: 2, label: t("actions:sell") },
  ];

  return (
    <Box className={styles.filterPanel}>
      <Accordion multiple defaultValue={["salesItems", "orderType", "amount", "price"]}>
        <Accordion.Item value="salesItems">
          <Accordion.Control>{t("product:productType")}</Accordion.Control>
          <Accordion.Panel>
            {isLoadingSalesItems ? (
              <Skeleton height={200} />
            ) : (
              <div className={styles.checkboxGroup}>
                {salesItems?.map((item) => (
                  <Checkbox
                    key={item.salesItemId}
                    label={item.salesItemTranslated}
                    checked={selectedSalesItemId === item.salesItemId}
                    onChange={() => onSalesItemChange(item.salesItemId)}
                  />
                ))}
              </div>
            )}
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="orderType">
          <Accordion.Control>{t("commonOrders:buyOrSell")}</Accordion.Control>
          <Accordion.Panel>
            <div className={styles.checkboxGroup}>
              {orderTypeOptions.map((orderType) => (
                <Checkbox
                  key={orderType.id}
                  label={orderType.label}
                  checked={selectedOrderTypes.includes(orderType.id)}
                  onChange={() => onOrderTypeChange(orderType.id)}
                />
              ))}
            </div>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="amount">
          <Accordion.Control>{t("common:amount")}</Accordion.Control>
          <Accordion.Panel>
            <NumberInput
              label={"Min"}
              placeholder={"Min"}
              value={localMinAmount}
              onChange={(value) => setLocalMinAmount(value === "" ? undefined : Number(value))}
              min={0}
              styles={{ root: { marginBottom: "0.75rem" } }}
            />
            <NumberInput
              label={"Max"}
              placeholder={"Max"}
              value={localMaxAmount}
              onChange={(value) => setLocalMaxAmount(value === "" ? undefined : Number(value))}
              min={0}
            />
          </Accordion.Panel>
        </Accordion.Item>

        {/* <Accordion.Item value="price">
          <Accordion.Control>{t("common:price")}</Accordion.Control>
          <Accordion.Panel>
            <NumberInput
              label={t("orders:from")}
              placeholder={t("orders:from")}
              value={localMinPrice}
              onChange={(value) => setLocalMinPrice(value === "" ? undefined : Number(value))}
              min={0}
              styles={{ root: { marginBottom: "0.75rem" } }}
            />
            <NumberInput
              label={t("orders:to")}
              placeholder={t("orders:to")}
              value={localMaxPrice}
              onChange={(value) => setLocalMaxPrice(value === "" ? undefined : Number(value))}
              min={0}
            />
          </Accordion.Panel>
        </Accordion.Item> */}

        {/* Dynamic Property Sections - Each propConfig gets its own accordion */}
        {selectedSalesItemId &&
          (isLoadingProperties ? (
            <Accordion.Item value="propertiesLoading">
              <Accordion.Control>{t("common:properties")}</Accordion.Control>
              <Accordion.Panel>
                <Skeleton height={150} />
              </Accordion.Panel>
            </Accordion.Item>
          ) : (
            dynamicProperties?.map((propConfig) => (
              <Accordion.Item
                key={propConfig.config.translatedLabel}
                value={`property_${propConfig.config.translatedLabel}`}
              >
                <Accordion.Control>{propConfig.config.translatedLabel}</Accordion.Control>
                <Accordion.Panel>
                  <div className={styles.checkboxGroup}>
                    {propConfig.properties.map((prop) => (
                      <Checkbox
                        key={prop.id}
                        label={prop.value}
                        checked={selectedPropertyIds.includes(prop.id)}
                        onChange={() => onPropertyChange(prop.id)}
                      />
                    ))}
                  </div>
                </Accordion.Panel>
              </Accordion.Item>
            ))
          ))}
      </Accordion>

      {/* Clear All Button */}
      {(selectedPropertyIds.length > 0 ||
        selectedOrderTypes.length > 0 ||
        minAmount !== undefined ||
        maxAmount !== undefined ||
        minPrice !== undefined ||
        maxPrice !== undefined) && (
        <CTA onClick={onClearAll} className={styles.clearButton} variant="outline">
          {t("common:clearFilter")}
        </CTA>
      )}
    </Box>
  );
};

export default FilterPanel;
